import { useTranslation } from 'react-i18next';
import { useParams, Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useToastWarning } from '~/utils/useToastWarning';
import Heading from '~/components/Shared/Heading/Heading';
import Page from '~/components/Shared/Layout/Page/Page';
import { Pagination } from '~/ui';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TrashIcon from '~/assets/svg/newSvg/basket.svg?react';
import EmptyImageIcon from '~/assets/svg/newSvg/empty-image.svg?react';
import PhotoIcon from '~/assets/svg/newSvg/picture.svg?react';
import FolderIcon from '~/assets/svg/newSvg/folder.svg?react';
import FolderPlusIcon from '~/assets/svg/newSvg/folder-plus.svg?react';
import ArrowLeftIcon from '~/assets/svg/newSvg/arrow-light-left.svg?react';
import ArrowRightIcon from '~/assets/svg/newSvg/arrow-light-right.svg?react';
import { Modal } from '~/components/Shared/Modal/Modal';
import { Portal } from '~/components/Shared/Portal/Portal';
import CloseIcon from '~/assets/svg/newSvg/cross-close.svg?react';
import SelectedIcon from '~/assets/svg/newSvg/selected-icon.svg?react';
import styles from './PhotoStoragePhotos.module.scss';
import folderStyles from './PhotoStorage.module.scss';

interface IPhoto {
  id: number;
  photo_id: number;
  asset_id: number;
  module: string;
  url: string;
}

interface IFolder {
  id: number;
  name: string;
  parent_id: number | null;
  photos_count: number;
  children_count: number;
}

interface IFolderStackItem {
  id: number;
  name: string;
}

interface IDragItem {
  type: 'photo' | 'folder';
  id: number;
  batchCount?: number;
}

const DRAG_DATA_KEY = 'application/x-photo-storage';
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

// ─── SortableFolder ──────────────────────────────────────────────────────────

interface SortableFolderProps {
  folder: IFolder;
  dragOverTarget: number | 'breadcrumb' | null;
  draggingItem: IDragItem | null;
  renamingFolderId: number | null;
  renamingName: string;
  renameInputRef: React.RefObject<HTMLInputElement>;
  wasDraggingFolders?: React.RefObject<boolean>;
  onEnterFolder: (folder: IFolder) => void;
  onStartRename: (folder: IFolder, e: React.MouseEvent) => void;
  onDeleteConfirm: (id: number) => void;
  onRenameChange: (name: string) => void;
  onRenameSubmit: (args: { id: number; name: string }) => void;
  onRenameCancelId: () => void;
  onDropZoneDragOver: (target: number | 'breadcrumb') => (e: React.DragEvent) => void;
  onDropZoneDragLeave: (e: React.DragEvent) => void;
  onDrop: (targetFolderId: number | null) => (e: React.DragEvent) => void;
  t: (key: string) => string;
}

const SortableFolder = ({
  folder,
  dragOverTarget,
  draggingItem,
  renamingFolderId,
  renamingName,
  renameInputRef,
  onEnterFolder,
  onStartRename,
  onDeleteConfirm,
  onRenameChange,
  onRenameSubmit,
  onRenameCancelId,
  onDropZoneDragOver,
  onDropZoneDragLeave,
  onDrop,
  t,
}: SortableFolderProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortDragging,
  } = useSortable({ id: folder.id });

  const sortStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortDragging ? 0.5 : 1,
    cursor: isSortDragging ? 'grabbing' : 'grab',
    zIndex: isSortDragging ? 1 : undefined,
  };

  return (
    <button
      ref={setNodeRef}
      style={sortStyle}
      {...attributes}
      {...listeners}
      type="button"
      className={[
        folderStyles.card,
        styles.folderCard,
        dragOverTarget === folder.id ? styles.isDragOver : '',
      ].filter(Boolean).join(' ')}
      onDragOver={onDropZoneDragOver(folder.id)}
      onDragLeave={onDropZoneDragLeave}
      onDrop={onDrop(folder.id)}
      onClick={() => {
        if (renamingFolderId === folder.id) return;
        onEnterFolder(folder);
      }}
    >
      <div className={styles.folderCardHeader}>
        <FolderIcon className={folderStyles.icon} />
        <div
          className={styles.folderCardActions}
          onPointerDown={e => e.stopPropagation()}
        >
          <button
            type="button"
            className={styles.folderActionBtn}
            title={t('rename')}
            onClick={e => onStartRename(folder, e)}
          >
            ✎
          </button>
          <button
            type="button"
            className={`${styles.folderActionBtn} ${styles.folderActionDelete}`}
            title={t('delete')}
            onClick={e => {
              e.stopPropagation();
              onDeleteConfirm(folder.id);
            }}
          >
            <TrashIcon className={styles.folderTrashIcon} />
          </button>
        </div>
      </div>
      {renamingFolderId === folder.id ? (
        <input
          ref={renameInputRef}
          className={styles.folderNameInput}
          value={renamingName}
          onChange={e => onRenameChange(e.target.value)}
          onClick={e => e.stopPropagation()}
          onPointerDown={e => e.stopPropagation()}
          onKeyDown={e => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              onRenameSubmit({ id: folder.id, name: renamingName.trim() || folder.name });
            }
            if (e.key === 'Escape') {
              onRenameCancelId();
            }
          }}
          onBlur={() => {
            if (renamingName.trim()) {
              onRenameSubmit({ id: folder.id, name: renamingName.trim() });
            } else {
              onRenameCancelId();
            }
          }}
        />
      ) : (
        <p className={folderStyles.name}>{folder.name}</p>
      )}
      <span className={folderStyles.count}>
        {folder.photos_count} {t('photos_short')}
        {folder.children_count > 0 && ` · ${folder.children_count} ${t('folders_short')}`}
      </span>
      {dragOverTarget === folder.id && draggingItem?.batchCount && draggingItem.batchCount > 1 && (
        <span className={styles.folderDropBadge}>{draggingItem.batchCount}</span>
      )}
    </button>
  );
};

// ─── SortablePhotoCard ────────────────────────────────────────────────────────

interface SortablePhotoCardProps {
  photo: IPhoto;
  index: number;
  isSelectMode: boolean;
  selectedPhotoIds: Set<number>;
  marqueeOverIds: Set<number>;
  movingPhotoIds: Set<number>;
  loadedIds: Set<number>;
  wasDraggingPhotos: React.RefObject<boolean>;
  photoCardRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  onPhotoCardClick: (photo: IPhoto, e: React.MouseEvent) => void;
  onDeleteConfirm: (id: number) => void;
  onLoad: (id: number) => void;
  t: (key: string) => string;
}

const SortablePhotoCard = ({
  photo,
  index,
  isSelectMode,
  selectedPhotoIds,
  marqueeOverIds,
  movingPhotoIds,
  loadedIds,
  wasDraggingPhotos,
  photoCardRefsMap,
  onPhotoCardClick,
  onDeleteConfirm,
  onLoad,
  t,
}: SortablePhotoCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortDragging,
  } = useSortable({ id: photo.id });

  const sortStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortDragging ? 0.5 : 1,
    cursor: isSortDragging ? 'grabbing' : 'grab',
    zIndex: isSortDragging ? 1 : undefined,
    animationDelay: `${Math.min(index * 30, 300)}ms`,
  };

  const setRefs = (el: HTMLDivElement | null) => {
    setNodeRef(el);
    if (!photoCardRefsMap.current) return;
    if (el) photoCardRefsMap.current.set(photo.id, el);
    else photoCardRefsMap.current.delete(photo.id);
  };

  return (
    <div
      ref={setRefs}
      data-photo-card=""
      className={[
        styles.photoCard,
        selectedPhotoIds.has(photo.id) ? styles.photoCardSelected : '',
        marqueeOverIds.has(photo.id) && !selectedPhotoIds.has(photo.id) ? styles.photoCardMarqueeHover : '',
        movingPhotoIds.has(photo.id) ? styles.photoCardMoving : '',
      ].filter(Boolean).join(' ')}
      style={sortStyle}
      {...attributes}
      {...listeners}
      onClick={e => {
        if (wasDraggingPhotos.current) return;
        onPhotoCardClick(photo, e);
      }}
    >
      {isSelectMode && (
        <div className={`${styles.photoCheckbox} ${selectedPhotoIds.has(photo.id) ? styles.photoCheckboxChecked : ''}`}>
          {selectedPhotoIds.has(photo.id) && (
            <svg viewBox="0 0 12 10" fill="none" className={styles.photoCheckmark}>
              <path d="M1 5l3.5 3.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
      )}
      {!loadedIds.has(photo.id) && (
        <div className={styles.shimmer} />
      )}
      <button
        type="button"
        className={styles.photoButton}
        style={{ cursor: isSelectMode ? 'default' : 'pointer' }}
        onPointerDown={e => e.stopPropagation()}
      >
        <img
          src={photo.url}
          alt=""
          className={styles.photoImage}
          onLoad={() => onLoad(photo.id)}
          onError={e => {
            onLoad(photo.id);
            (e.currentTarget as HTMLImageElement).style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.add(styles.brokenVisible);
          }}
        />
        <div className={styles.brokenPlaceholder}>
          <EmptyImageIcon className={styles.brokenIcon} />
          <span className={styles.brokenText}>{t('failed_to_load')}</span>
        </div>
      </button>
      <button
        type="button"
        className={styles.deleteButton}
        onPointerDown={e => e.stopPropagation()}
        onClick={e => { e.stopPropagation(); onDeleteConfirm(photo.id); }}
      >
        <TrashIcon className={styles.deleteIcon} />
      </button>
    </div>
  );
};

// ─── Route guard ─────────────────────────────────────────────────────────────

const PhotoStoragePhotosRedirectGuard = () => {
  const { module, assetId, entityId } = useParams<{ module: string; assetId: string; entityId?: string }>();
  if ((module === 'accommodation' || module === 'food') && !entityId) {
    return <Navigate to={`/documents/photo-storage/${module}/${assetId}/entities`} replace />;
  }
  return <PhotoStoragePhotos />;
};

export default PhotoStoragePhotosRedirectGuard;

const PhotoStoragePhotos = () => {
  const { t } = useTranslation();
  const { module, assetId, entityId } = useParams<{ module: string; assetId: string; entityId?: string }>();

  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const toastWarning = useToastWarning();
  const client = useQueryClient();

  // Folder navigation stack
  const [folderStack, setFolderStack] = useState<IFolderStackItem[]>([]);
  const currentFolderId = folderStack.length > 0 ? folderStack[folderStack.length - 1].id : null;

  // Drag-and-drop state
  const [draggingItem, setDraggingItem] = useState<IDragItem | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<number | 'breadcrumb' | null>(null);

  // Folder creation
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const newFolderInputRef = useRef<HTMLInputElement>(null);

  // Folder rename
  const [renamingFolderId, setRenamingFolderId] = useState<number | null>(null);
  const [renamingName, setRenamingName] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Folder delete confirm
  const [deleteFolderConfirmId, setDeleteFolderConfirmId] = useState<number | null>(null);

  // Select mode state
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<number>>(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  // Move progress state
  const [movingCount, setMovingCount] = useState(0);
  const [movingDone, setMovingDone] = useState(false);
  const [movingPhotoIds, setMovingPhotoIds] = useState<Set<number>>(new Set());

  // Photos state
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [displayedPhoto, setDisplayedPhoto] = useState<IPhoto | null>(null);
  const [incomingPhoto, setIncomingPhoto] = useState<IPhoto | null>(null);
  const [previewBroken, setPreviewBroken] = useState(false);
  const [navDir, setNavDir] = useState<'left' | 'right'>('right');
  const [animKey, setAnimKey] = useState(0);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [loadedIds, setLoadedIds] = useState<Set<number>>(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isFileDragging, setIsFileDragging] = useState(false);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  // Marquee selection
  const gridRef = useRef<HTMLDivElement>(null);
  const photoCardRefsMap = useRef<Map<number, HTMLDivElement>>(new Map());
  const marqueeStartRef = useRef<{ clientX: number; clientY: number } | null>(null);
  const [marqueeRect, setMarqueeRect] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const [marqueeOverIds, setMarqueeOverIds] = useState<Set<number>>(new Set());

  // DnD sort sensors (shared for both grids)
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  // wasDragging refs — prevent navigation after drag
  const wasDraggingFolders = useRef(false);
  const wasDraggingPhotos = useRef(false);

  // Local sortable state for folders and photos
  const [folderItems, setFolderItems] = useState<IFolder[]>([]);
  const [photoItems, setPhotoItems] = useState<IPhoto[]>([]);

  // Folders query
  const foldersQueryKey = ['photo-storage-folders', module, assetId, currentFolderId];
  const { data: folders = [], isLoading: foldersLoading } = useQuery({
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (currentFolderId !== null) params.parent_id = String(currentFolderId);
      const { data } = await instance.get(
        `admin/photo-storage/${module}/${assetId}/folders`,
        { params }
      );
      return data as IFolder[];
    },
    queryKey: foldersQueryKey,
    onError: (error: unknown) => toastError(error),
    enabled: module === 'announcement' || module === 'knowledge_base' || module === 'promo_loyalty' || module === 'food' || module === 'ifcm_wares',
  });

  const supportsFolders = module === 'announcement' || module === 'knowledge_base' || module === 'promo_loyalty' || module === 'food' || module === 'ifcm_wares';

  // Sync folder/photo local items with query data
  useEffect(() => {
    setFolderItems(folders ?? []);
  }, [folders]);

  const photosQueryKey = ['photo-storage-photos', module, assetId, entityId, page, perPage, currentFolderId];

  // Folders sort mutation
  const { mutate: saveFoldersSort } = useMutation(
    async (ids: number[]) => {
      const { data } = await instance.post(
        `admin/photo-storage/${module}/${assetId}/folders/sort`,
        { ids }
      );
      return data;
    },
    {
      onSuccess: () => {
        client.invalidateQueries(foldersQueryKey);
      },
      onError: (error: unknown) => {
        toastError(error);
        setFolderItems(folders ?? []);
      },
    }
  );

  // Photos sort mutation
  const { mutate: savePhotosSort } = useMutation(
    async (ids: number[]) => {
      const { data } = await instance.post(
        `admin/photo-storage/${module}/${assetId}/photos/sort`,
        { ids }
      );
      return data;
    },
    {
      onSuccess: () => {
        client.invalidateQueries(photosQueryKey);
      },
      onError: (error: unknown) => {
        toastError(error);
        setPhotoItems(photos?.data ?? []);
      },
    }
  );

  // DnD handlers for folders
  const handleFolderDragEnd = (event: DragEndEvent) => {
    if (wasDraggingFolders.current) {
      const cancelClick = (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        document.removeEventListener('click', cancelClick, true);
      };
      document.addEventListener('click', cancelClick, true);
      wasDraggingFolders.current = false;
    }

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = folderItems.findIndex(f => f.id === active.id);
    const newIndex = folderItems.findIndex(f => f.id === over.id);
    const reordered = arrayMove(folderItems, oldIndex, newIndex);
    setFolderItems(reordered);
    saveFoldersSort(reordered.map(f => f.id));
  };

  // DnD handlers for photos
  const handlePhotosDragEnd = (event: DragEndEvent) => {
    if (wasDraggingPhotos.current) {
      const cancelClick = (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        document.removeEventListener('click', cancelClick, true);
      };
      document.addEventListener('click', cancelClick, true);
      wasDraggingPhotos.current = false;
    }

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = photoItems.findIndex(p => p.id === active.id);
    const newIndex = photoItems.findIndex(p => p.id === over.id);
    const reordered = arrayMove(photoItems, oldIndex, newIndex);
    setPhotoItems(reordered);
    savePhotosSort(reordered.map(p => p.id));
  };

  const handlePageChange = (selectedPage: { selected: number }) => {
    setPage(selectedPage.selected + 1);
    setLoadedIds(new Set());
  };

  const handlePerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPerPage(Number(e.target.value));
    setPage(1);
    setLoadedIds(new Set());
  };

  const toggleSelectMode = () => {
    setIsSelectMode(prev => !prev);
    setSelectedPhotoIds(new Set());
  };

  const togglePhotoSelect = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedPhotoIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handlePhotoCardClick = (photo: IPhoto, e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      setSelectedPhotoIds(prev => {
        const next = new Set(prev);
        if (next.has(photo.id)) next.delete(photo.id); else next.add(photo.id);
        if (next.size > 0) setIsSelectMode(true);
        return next;
      });
      return;
    }
    if (isSelectMode) { togglePhotoSelect(photo.id, e); return; }
    openPreview(photo);
  };

  const handleGridMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    if ((e.target as Element).closest('[data-photo-card], button, input, a')) return;
    marqueeStartRef.current = { clientX: e.clientX, clientY: e.clientY };
    e.preventDefault();
  };

  const moduleName = t(`photo_storage_${module}`);

  const { data: photos, isLoading } = useQuery({
    queryFn: async () => {
      const params: Record<string, string | number> = { perPage, page };
      if (entityId) params.entity_id = entityId;
      if (supportsFolders) params.folder_id = currentFolderId ?? '';
      const { data } = await instance.get(
        `admin/photo-storage/${module}/${assetId}`,
        { params }
      );
      return data;
    },
    queryKey: photosQueryKey,
    onError: (error: unknown) => toastError(error),
  });

  // Sync photo items with query data
  useEffect(() => {
    setPhotoItems(photos?.data ?? []);
  }, [photos]);

  const photoList: IPhoto[] = photoItems;

  // Folder navigation
  const enterFolder = (folder: IFolder) => {
    setFolderStack(prev => [...prev, { id: folder.id, name: folder.name }]);
    setPage(1);
    setLoadedIds(new Set());
  };

  const navigateToBreadcrumb = (index: number) => {
    setFolderStack(prev => prev.slice(0, index));
    setPage(1);
    setLoadedIds(new Set());
  };

  // Drag-and-drop: move photo to folder
  const { mutateAsync: movePhotoMutation } = useMutation(
    async ({ photoId, folderId }: { photoId: number; folderId: number | null }) => {
      await instance.patch(`admin/photo-storage/photos/${photoId}/move`, { folder_id: folderId });
    },
    {
      onError: (error: unknown) => toastError(error),
    }
  );

  // Drag-and-drop: move folder to another folder
  const { mutateAsync: moveFolderMutation } = useMutation(
    async ({ folderId, parentId }: { folderId: number; parentId: number | null }) => {
      await instance.put(`admin/photo-storage/folders/${folderId}`, { parent_id: parentId });
    },
    {
      onSuccess: () => {
        client.invalidateQueries(foldersQueryKey);
      },
      onError: (error: unknown) => toastError(error),
    }
  );

  const handleDropZoneDragOver = (target: number | 'breadcrumb') => (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(DRAG_DATA_KEY)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget(target as number | 'breadcrumb');
  };

  const handleDropZoneDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverTarget(null);
    }
  };

  const handleDrop = (targetFolderId: number | null) => async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTarget(null);
    setDraggingItem(null);

    const raw = e.dataTransfer.getData(DRAG_DATA_KEY);
    if (!raw) return;

    try {
      const item: IDragItem = JSON.parse(raw);

      if (item.type === 'photo') {
        const ids: number[] = (item as any).batchIds ?? [item.id];
        setMovingCount(ids.length);
        setMovingPhotoIds(new Set(ids));
        try {
          await Promise.all(ids.map((pid: number) => movePhotoMutation({ photoId: pid, folderId: targetFolderId })));
          if (ids.length > 1) { setSelectedPhotoIds(new Set()); setIsSelectMode(false); }
          // Await the actual data refresh so the toast stays until the UI is updated
          await Promise.all([
            client.refetchQueries(photosQueryKey),
            client.refetchQueries(foldersQueryKey),
          ]);
          setMovingPhotoIds(new Set());
          setMovingDone(true);
          await new Promise(resolve => setTimeout(resolve, 2000));
        } finally {
          setMovingCount(0);
          setMovingDone(false);
          setMovingPhotoIds(new Set());
        }
      } else if (item.type === 'folder' && item.id !== targetFolderId) {
        await moveFolderMutation({ folderId: item.id, parentId: targetFolderId });
      }
    } catch {
      // ignore parse errors
    }
  };

  // Create folder
  const { mutateAsync: createFolderMutation } = useMutation(
    async (name: string) => {
      const { data } = await instance.post(
        `admin/photo-storage/${module}/${assetId}/folders`,
        { name, parent_id: currentFolderId ?? undefined }
      );
      return data;
    },
    {
      onSuccess: () => {
        client.invalidateQueries(foldersQueryKey);
        setIsCreatingFolder(false);
        setNewFolderName('');
      },
      onError: (error: unknown) => toastError(error),
    }
  );

  const handleCreateFolderSubmit = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    await createFolderMutation(name);
  };

  // Rename folder
  const { mutateAsync: renameFolderMutation } = useMutation(
    async ({ id, name }: { id: number; name: string }) => {
      await instance.put(`admin/photo-storage/folders/${id}`, { name });
    },
    {
      onSuccess: () => {
        client.invalidateQueries(foldersQueryKey);
        setRenamingFolderId(null);
        setRenamingName('');
      },
      onError: (error: unknown) => toastError(error),
    }
  );

  const startRename = (folder: IFolder, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenamingFolderId(folder.id);
    setRenamingName(folder.name);
    setTimeout(() => renameInputRef.current?.focus(), 50);
  };

  // Delete folder
  const { mutateAsync: deleteFolderMutation } = useMutation(
    async (id: number) => {
      await instance.delete(`admin/photo-storage/folders/${id}`);
    },
    {
      onSuccess: () => {
        client.invalidateQueries(foldersQueryKey);
        client.invalidateQueries(['photo-storage-photos']);
        setDeleteFolderConfirmId(null);
        toastSuccess(t('deleted_successfully'));
      },
      onError: (error: unknown) => toastError(error),
    }
  );

  // Creating folder input focus
  useEffect(() => {
    if (isCreatingFolder) {
      setTimeout(() => newFolderInputRef.current?.focus(), 50);
    }
  }, [isCreatingFolder]);

  // Photo preview navigation
  const openPreview = (photo: IPhoto) => {
    setDisplayedPhoto(photo);
    setIncomingPhoto(null);
    setPreviewBroken(false);
    setAnimKey(k => k + 1);
  };

  const navigate = useCallback((dir: 1 | -1) => {
    if (!photoList.length) return;
    const base = incomingPhoto || displayedPhoto;
    if (!base) return;
    const idx = photoList.findIndex(p => p.id === base.id);
    const next = photoList[(idx + dir + photoList.length) % photoList.length];
    setNavDir(dir === 1 ? 'right' : 'left');
    setPreviewBroken(false);
    setIncomingPhoto(next);
  }, [displayedPhoto, incomingPhoto, photoList]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!displayedPhoto) return;
      if (e.key === 'ArrowLeft') navigate(-1);
      if (e.key === 'ArrowRight') navigate(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [displayedPhoto, navigate]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      const start = marqueeStartRef.current;
      const grid = gridRef.current;
      if (!start || !grid) return;
      const gb = grid.getBoundingClientRect();
      const sx = start.clientX - gb.left;
      const sy = start.clientY - gb.top;
      const cx = e.clientX - gb.left;
      const cy = e.clientY - gb.top;
      if (Math.abs(cx - sx) > 4 || Math.abs(cy - sy) > 4) {
        const mL = Math.min(sx, cx), mT = Math.min(sy, cy), mR = Math.max(sx, cx), mB = Math.max(sy, cy);
        setMarqueeRect({ left: mL, top: mT, width: mR - mL, height: mB - mT });
        const hoverIds = new Set<number>();
        photoCardRefsMap.current.forEach((el, id) => {
          const b = el.getBoundingClientRect();
          const cL = b.left - gb.left, cT = b.top - gb.top;
          const cR = cL + b.width, cB = cT + b.height;
          if (cL < mR && cR > mL && cT < mB && cB > mT) hoverIds.add(id);
        });
        setMarqueeOverIds(hoverIds);
      }
    };
    const onMouseUp = (e: MouseEvent) => {
      const start = marqueeStartRef.current;
      if (!start) return;
      const grid = gridRef.current;
      if (grid) {
        const gb = grid.getBoundingClientRect();
        const sx = start.clientX - gb.left;
        const sy = start.clientY - gb.top;
        const ex = e.clientX - gb.left;
        const ey = e.clientY - gb.top;
        const mL = Math.min(sx, ex), mT = Math.min(sy, ey), mR = Math.max(sx, ex), mB = Math.max(sy, ey);
        if (mR - mL > 5 && mB - mT > 5) {
          const newIds = new Set<number>();
          photoCardRefsMap.current.forEach((el, id) => {
            const b = el.getBoundingClientRect();
            const cL = b.left - gb.left, cT = b.top - gb.top;
            const cR = cL + b.width, cB = cT + b.height;
            if (cL < mR && cR > mL && cT < mB && cB > mT) newIds.add(id);
          });
          if (newIds.size > 0) {
            setIsSelectMode(true);
            setSelectedPhotoIds(prev => new Set([...prev, ...newIds]));
          }
        }
      }
      marqueeStartRef.current = null;
      setMarqueeRect(null);
      setMarqueeOverIds(new Set());
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => { document.removeEventListener('mousemove', onMouseMove); document.removeEventListener('mouseup', onMouseUp); };
  }, []);

  useEffect(() => {
    if (isUploading) {
      setUploadProgress(0);
      progressTimerRef.current = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 85) {
            if (progressTimerRef.current) clearInterval(progressTimerRef.current);
            return 85;
          }
          return prev + Math.random() * 8 + 3;
        });
      }, 300);
    } else {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      if (uploadProgress > 0) {
        setUploadProgress(100);
        setTimeout(() => setUploadProgress(0), 600);
      }
    }
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [isUploading]);

  const { mutateAsync: uploadPhotos } = useMutation(
    async (files: File[]) => {
      const formData = new FormData();
      files.forEach(file => formData.append('photos[]', file));
      if (entityId) formData.append('entity_id', entityId);
      if (supportsFolders && currentFolderId !== null) {
        formData.append('folder_id', String(currentFolderId));
      }
      const { data } = await instance.post(
        `admin/photo-storage/${module}/${assetId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return data;
    },
    {
      onSuccess: () => {
        toastSuccess(t('photo_uploaded'));
        client.invalidateQueries(photosQueryKey);
        client.invalidateQueries(foldersQueryKey);
        client.invalidateQueries(['photo-storage-assets']);
        client.invalidateQueries(['photo-storage-modules']);
      },
      onError: (error: unknown) => toastError(error),
    }
  );

  const handleUploadFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    setIsUploading(true);
    try {
      await uploadPhotos(Array.from(files));
    } finally {
      setIsUploading(false);
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (!e.dataTransfer.types.includes('Files')) return;
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsFileDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    if (!e.dataTransfer.types.includes('Files')) return;
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsFileDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleFileDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    // Only handle OS file drops, not in-app drags
    if (!e.dataTransfer.types.includes('Files') || e.dataTransfer.getData(DRAG_DATA_KEY)) return;
    dragCounterRef.current = 0;
    setIsFileDragging(false);
    const all = Array.from(e.dataTransfer.files);
    const files = all.filter(f => ALLOWED_MIME_TYPES.includes(f.type));
    if (all.length > files.length) {
      toastWarning(t('only_images_allowed'));
    }
    if (!files.length) return;
    setIsUploading(true);
    try {
      await uploadPhotos(files);
    } finally {
      setIsUploading(false);
    }
  };

  const { mutateAsync: deletePhoto } = useMutation(
    async (id: number) => {
      await instance.delete(`admin/photo-storage/${id}`);
    },
    {
      onSuccess: () => {
        toastSuccess(t('deleted_successfully'));
        client.invalidateQueries(photosQueryKey);
        client.invalidateQueries(foldersQueryKey);
        client.invalidateQueries(['photo-storage-assets']);
        client.invalidateQueries(['photo-storage-modules']);
        setDeleteConfirmId(null);
      },
      onError: (error: unknown) => toastError(error),
    }
  );

  const { mutateAsync: bulkDeleteMutation, isLoading: isBulkDeleting } = useMutation(
    async (ids: number[]) => {
      await Promise.all(ids.map(id => instance.delete(`admin/photo-storage/${id}`)));
    },
    {
      onSuccess: () => {
        client.invalidateQueries(photosQueryKey);
        client.invalidateQueries(foldersQueryKey);
        client.invalidateQueries(['photo-storage-assets']);
        client.invalidateQueries(['photo-storage-modules']);
        setSelectedPhotoIds(new Set());
        setIsSelectMode(false);
        setBulkDeleteConfirm(false);
        toastSuccess(t('deleted_successfully'));
      },
      onError: (error: unknown) => toastError(error),
    }
  );

  const hasFolders = supportsFolders;
  const showFolderSection = hasFolders && (folderItems.length > 0 || isCreatingFolder || foldersLoading);

  return (
    <Page heading={<Heading text={`${t('photo_storage')} — ${moduleName}`} />}>
      <div
        className={styles.container}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleFileDrop}
      >
        {isFileDragging && (
          <div className={styles.dropOverlay}>
            <div className={styles.dropOverlayCard}>
              <div className={styles.dropOverlayIconWrap}>
                <PhotoIcon className={styles.dropOverlayIcon} />
              </div>
              <p className={styles.dropOverlayText}>{t('drop_photos_here')}</p>
              <p className={styles.dropOverlayHint}>{t('drop_photos_hint')}</p>
            </div>
          </div>
        )}
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleUploadFiles}
          className={styles.hiddenInput}
        />

        {/* Toolbar */}
        <div className={styles.toolbar}>
          {/* Breadcrumb */}
          {hasFolders && folderStack.length > 0 && (
            <div className={styles.breadcrumb}>
              <button
                type="button"
                className={`${styles.breadcrumbItem} ${dragOverTarget === 'breadcrumb' && folderStack.length > 0 ? styles.breadcrumbDragOver : ''}`}
                onClick={() => navigateToBreadcrumb(0)}
                onDragOver={handleDropZoneDragOver('breadcrumb')}
                onDragLeave={handleDropZoneDragLeave}
                onDrop={handleDrop(null)}
              >
                {t('root_folder')}
              </button>
              {folderStack.map((item, index) => (
                <span key={item.id} className={styles.breadcrumbSeparator}>
                  <span className={styles.breadcrumbArrow}>/</span>
                  {index < folderStack.length - 1 ? (
                    <button
                      type="button"
                      className={`${styles.breadcrumbItem} ${dragOverTarget === item.id ? styles.breadcrumbDragOver : ''}`}
                      onClick={() => navigateToBreadcrumb(index + 1)}
                      onDragOver={handleDropZoneDragOver(item.id)}
                      onDragLeave={handleDropZoneDragLeave}
                      onDrop={handleDrop(item.id)}
                    >
                      {item.name}
                    </button>
                  ) : (
                    <span className={styles.breadcrumbCurrent}>{item.name}</span>
                  )}
                </span>
              ))}
            </div>
          )}

          <div className={styles.toolbarActions}>
            {hasFolders && (
              <>
                <button
                  type="button"
                  className={styles.createFolderButton}
                  onClick={() => setIsCreatingFolder(true)}
                >
                  <FolderPlusIcon className={styles.createFolderIcon} />
                  {t('create_folder')}
                </button>
                <span className={styles.toolbarDivider} />
              </>
            )}
            <button
              type="button"
              className={`${styles.selectModeButton} ${isSelectMode ? styles.selectModeButtonActive : ''}`}
              onClick={toggleSelectMode}
            >
              {isSelectMode ? (
                <>
                  <CloseIcon className={styles.selectModeIcon} />
                  {t('cancel_selection')}
                </>
              ) : (
                <>
                  <SelectedIcon className={styles.selectModeIcon} />
                  {t('select')}
                </>
              )}
            </button>
            {isUploading ? (
              <div className={styles.uploadProgress}>
                <div className={styles.uploadProgressHeader}>
                  <span className={styles.uploadProgressLabel}>
                    <PhotoIcon className={styles.uploadProgressIcon} />
                    {t('upload_photos')}
                  </span>
                  <span className={styles.uploadProgressPercent}>
                    {Math.min(Math.round(uploadProgress), 100)}%
                  </span>
                </div>
                <div className={styles.uploadProgressTrack}>
                  <div
                    className={styles.uploadProgressFill}
                    style={{ width: `${Math.min(Math.round(uploadProgress), 100)}%` }}
                  />
                </div>
              </div>
            ) : (
              <button
                type="button"
                className={styles.uploadButton}
                onClick={() => uploadInputRef.current?.click()}
              >
                <PhotoIcon className={styles.uploadButtonIcon} />
                {t('upload_photos')}
              </button>
            )}
          </div>
        </div>

        {/* Folders section */}
        {hasFolders && (
          <div className={styles.foldersSection}>
            <DndContext
              sensors={dndSensors}
              collisionDetection={closestCenter}
              onDragStart={() => { wasDraggingFolders.current = true; }}
              onDragEnd={handleFolderDragEnd}
            >
              <SortableContext items={folderItems.map(f => f.id)} strategy={rectSortingStrategy}>
                <div className={folderStyles.grid}>
                  {/* New folder input card */}
                  {isCreatingFolder && (
                    <div className={styles.newFolderCard}>
                      <div className={styles.newFolderHeader}>
                        <FolderIcon className={styles.newFolderIcon} />
                        <span className={styles.newFolderLabel}>{t('new_folder')}</span>
                      </div>
                      <input
                        ref={newFolderInputRef}
                        className={styles.folderNameInput}
                        value={newFolderName}
                        onChange={e => setNewFolderName(e.target.value)}
                        placeholder={t('folder_name')}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleCreateFolderSubmit();
                          if (e.key === 'Escape') {
                            setIsCreatingFolder(false);
                            setNewFolderName('');
                          }
                        }}
                        onBlur={() => {
                          if (!newFolderName.trim()) {
                            setIsCreatingFolder(false);
                            setNewFolderName('');
                          }
                        }}
                      />
                      <div className={styles.newFolderActions}>
                        <button
                          type="button"
                          className={styles.newFolderConfirm}
                          onClick={handleCreateFolderSubmit}
                        >
                          {t('create')}
                        </button>
                        <button
                          type="button"
                          className={styles.newFolderCancel}
                          onClick={() => {
                            setIsCreatingFolder(false);
                            setNewFolderName('');
                          }}
                        >
                          {t('cancel')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Folder cards */}
                  {foldersLoading
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className={folderStyles.cardShimmer} style={{ animationDelay: `${i * 0.05}s` }} />
                      ))
                    : folderItems.map(folder => (
                        <SortableFolder
                          key={folder.id}
                          folder={folder}
                          dragOverTarget={dragOverTarget}
                          draggingItem={draggingItem}
                          renamingFolderId={renamingFolderId}
                          renamingName={renamingName}
                          renameInputRef={renameInputRef}
                          wasDraggingFolders={wasDraggingFolders}
                          onEnterFolder={enterFolder}
                          onStartRename={startRename}
                          onDeleteConfirm={id => setDeleteFolderConfirmId(id)}
                          onRenameChange={setRenamingName}
                          onRenameSubmit={renameFolderMutation}
                          onRenameCancelId={() => setRenamingFolderId(null)}
                          onDropZoneDragOver={handleDropZoneDragOver}
                          onDropZoneDragLeave={handleDropZoneDragLeave}
                          onDrop={handleDrop}
                          t={t}
                        />
                      ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}

        {/* Photos section */}
        {isLoading ? (
          <div className={styles.grid}>
            {Array.from({ length: perPage }).map((_, i) => (
              <div key={i} className={styles.photoCard} style={{ animationDelay: `${Math.min(i * 20, 400)}ms` }}>
                <div className={styles.shimmer} />
              </div>
            ))}
          </div>
        ) : (
          <>
            {!photoItems.length && folderItems.length === 0 && !isCreatingFolder ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <PhotoIcon className={styles.emptyPhotoIcon} />
                </div>
                <p className={styles.emptyTitle}>{t('no_photos_yet')}</p>
                <p className={styles.emptySubtitle}>{t('upload_first_photo')}</p>
                {isUploading ? (
                  <div className={styles.emptyUploadProgress}>
                    <div className={styles.emptyProgressHeader}>
                      <span className={styles.emptyProgressLabel}>
                        <PhotoIcon className={styles.emptyProgressIcon} />
                        {t('upload_photos')}
                      </span>
                      <span className={styles.emptyProgressPercent}>
                        {Math.min(Math.round(uploadProgress), 100)}%
                      </span>
                    </div>
                    <div className={styles.emptyProgressTrack}>
                      <div
                        className={styles.emptyProgressFill}
                        style={{ width: `${Math.min(Math.round(uploadProgress), 100)}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles.emptyUploadButton}
                    onClick={() => uploadInputRef.current?.click()}
                  >
                    <PhotoIcon className={styles.emptyUploadIcon} />
                    {t('upload_photos')}
                  </button>
                )}
              </div>
            ) : photoItems.length > 0 ? (
              <>
                {showFolderSection && (
                  <div className={styles.photosSectionLabel}>{t('photos_in_folder')}</div>
                )}
                <DndContext
                  sensors={dndSensors}
                  collisionDetection={closestCenter}
                  onDragStart={() => { wasDraggingPhotos.current = true; }}
                  onDragEnd={handlePhotosDragEnd}
                >
                  <SortableContext items={photoItems.map(p => p.id)} strategy={rectSortingStrategy}>
                    <div
                      ref={gridRef}
                      className={styles.grid}
                      onMouseDown={handleGridMouseDown}
                    >
                      {marqueeRect && (
                        <div
                          className={styles.marqueeRect}
                          style={{ left: marqueeRect.left, top: marqueeRect.top, width: marqueeRect.width, height: marqueeRect.height }}
                        />
                      )}
                      {photoItems.map((photo: IPhoto, i: number) => (
                        <SortablePhotoCard
                          key={photo.id}
                          photo={photo}
                          index={i}
                          isSelectMode={isSelectMode}
                          selectedPhotoIds={selectedPhotoIds}
                          marqueeOverIds={marqueeOverIds}
                          movingPhotoIds={movingPhotoIds}
                          loadedIds={loadedIds}
                          wasDraggingPhotos={wasDraggingPhotos}
                          photoCardRefsMap={photoCardRefsMap}
                          onPhotoCardClick={handlePhotoCardClick}
                          onDeleteConfirm={id => setDeleteConfirmId(id)}
                          onLoad={id => setLoadedIds(prev => new Set(prev).add(id))}
                          t={t}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
                {isSelectMode && selectedPhotoIds.size > 0 && (
                  <div className={styles.actionBar}>
                    <span className={styles.actionBarCount}>
                      {t('selected_count', { count: selectedPhotoIds.size })}
                    </span>
                    <div className={styles.actionBarButtons}>
                      <button
                        type="button"
                        className={`${styles.actionBarBtn} ${styles.actionBarBtnDelete}`}
                        onClick={() => setBulkDeleteConfirm(true)}
                      >
                        {t('delete')} ({selectedPhotoIds.size})
                      </button>
                    </div>
                  </div>
                )}
                <div className={styles.paginationWrapper}>
                  <label className={styles.perPageWrapper}>
                    <span className={styles.perPageLabel}>{t('per_page')}</span>
                    <div className={styles.perPageSelectWrap}>
                      <select
                        className={styles.perPageSelect}
                        value={perPage}
                        onChange={handlePerPageChange}
                      >
                        {[10, 20, 50, 100].map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      <svg className={styles.perPageChevron} viewBox="0 0 10 6" fill="none">
                        <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </div>
                  </label>
                  <Pagination
                    pageCount={photos?.last_page || 1}
                    forcePage={page}
                    onPageChange={handlePageChange}
                  />
                </div>
              </>
            ) : null}
          </>
        )}
      </div>

      {/* Move progress toast */}
      {movingCount > 0 && (
        <Portal>
          <div className={`${styles.moveToast} ${movingDone ? styles.moveToastDone : ''}`}>
            {movingDone ? (
              <svg viewBox="0 0 16 12" fill="none" className={styles.moveToastCheck}>
                <path d="M1 6l4.5 4.5L15 1" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <div className={styles.moveToastSpinner} />
            )}
            <span className={styles.moveToastText}>
              {movingDone ? t('photos_moved') : t('moving_photos', { count: movingCount })}
            </span>
          </div>
        </Portal>
      )}

      {/* Photo lightbox */}
      {displayedPhoto && (
        <Portal>
          <div className={styles.lightbox} onClick={() => setDisplayedPhoto(null)}>
            <div
              className={styles.lightboxBg}
              style={{ backgroundImage: `url(${displayedPhoto.url})` }}
            />
            <div className={styles.lightboxDim} />
            {incomingPhoto && (
              <img
                key={`preload-${incomingPhoto.id}`}
                src={incomingPhoto.url}
                style={{ display: 'none' }}
                onLoad={() => {
                  setDisplayedPhoto(incomingPhoto);
                  setIncomingPhoto(null);
                  setAnimKey(k => k + 1);
                }}
                onError={() => {
                  setDisplayedPhoto(incomingPhoto);
                  setIncomingPhoto(null);
                  setPreviewBroken(true);
                  setAnimKey(k => k + 1);
                }}
              />
            )}
            <button
              type="button"
              className={styles.lightboxClose}
              onClick={() => setDisplayedPhoto(null)}
            >
              <CloseIcon className={styles.lightboxCloseIcon} />
            </button>
            <div className={styles.lightboxContent} onClick={e => e.stopPropagation()}>
              <button
                type="button"
                className={`${styles.lightboxNav} ${incomingPhoto ? styles.lightboxNavLoading : ''}`}
                onClick={() => navigate(-1)}
              >
                <ArrowLeftIcon className={styles.lightboxNavIcon} />
              </button>
              <div className={styles.lightboxImageWrapper}>
                {!previewBroken ? (
                  <img
                    key={animKey}
                    src={displayedPhoto.url}
                    alt=""
                    className={`${styles.lightboxImage} ${navDir === 'right' ? styles.slideFromRight : styles.slideFromLeft}`}
                  />
                ) : (
                  <div className={styles.lightboxBroken}>
                    <EmptyImageIcon className={styles.lightboxBrokenIcon} />
                    <span className={styles.lightboxBrokenText}>{t('failed_to_load')}</span>
                  </div>
                )}
              </div>
              <button
                type="button"
                className={`${styles.lightboxNav} ${incomingPhoto ? styles.lightboxNavLoading : ''}`}
                onClick={() => navigate(1)}
              >
                <ArrowRightIcon className={styles.lightboxNavIcon} />
              </button>
            </div>
            <div className={styles.lightboxCounter}>
              {incomingPhoto && <span className={styles.lightboxSpinner} />}
              {photoList.findIndex(p => p.id === displayedPhoto.id) + 1} / {photoList.length}
            </div>
          </div>
        </Portal>
      )}

      {/* Delete photo confirm */}
      <Modal
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
      >
        <div className={styles.confirmWrapper}>
          <p className={styles.confirmTitle}>{t('sure_delete_file')}</p>
          <p className={styles.confirmDescription}>
            {t('photo_will_be_deleted_everywhere')}
          </p>
          <div className={styles.confirmButtons}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => setDeleteConfirmId(null)}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className={styles.confirmDeleteButton}
              onClick={() => deleteConfirmId && deletePhoto(deleteConfirmId)}
            >
              {t('delete')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Bulk delete confirm */}
      <Modal isOpen={bulkDeleteConfirm} onClose={() => setBulkDeleteConfirm(false)}>
        <div className={styles.confirmWrapper}>
          <p className={styles.confirmTitle}>{t('sure_delete_selected', { count: selectedPhotoIds.size })}</p>
          <p className={styles.confirmDescription}>{t('bulk_delete_description')}</p>
          <div className={styles.confirmButtons}>
            <button type="button" className={styles.cancelButton} onClick={() => setBulkDeleteConfirm(false)}>
              {t('cancel')}
            </button>
            <button
              type="button"
              className={styles.confirmDeleteButton}
              disabled={isBulkDeleting}
              onClick={() => bulkDeleteMutation([...selectedPhotoIds])}
            >
              {isBulkDeleting ? '...' : t('delete')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete folder confirm */}
      <Modal
        isOpen={deleteFolderConfirmId !== null}
        onClose={() => setDeleteFolderConfirmId(null)}
      >
        <div className={styles.confirmWrapper}>
          <p className={styles.confirmTitle}>{t('sure_delete_folder')}</p>
          <p className={styles.confirmDescription}>
            {t('folder_delete_description')}
          </p>
          <div className={styles.confirmButtons}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => setDeleteFolderConfirmId(null)}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className={styles.confirmDeleteButton}
              onClick={() => deleteFolderConfirmId && deleteFolderMutation(deleteFolderConfirmId)}
            >
              {t('delete')}
            </button>
          </div>
        </div>
      </Modal>
    </Page>
  );
};
